import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, User, Wrench, Truck, Stethoscope, GraduationCap, HardHat, FileDown, PenLine, History, Shirt, Camera, KeyRound, ClipboardList, RefreshCw, FolderOpen } from "lucide-react";
import { AttachmentList, UploadButton, openFile } from "@/components/Attachments";
import EmployeeDocuments from "@/components/EmployeeDocuments";
import SignaturePad from "@/components/SignaturePad";
import HistoryList from "@/components/HistoryList";
import PortalAccessDialog from "@/components/PortalAccessDialog";
import { initials } from "@/lib/helpers";

function resizeImage(file, max = 320) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

const EMPTY = {
  name: "", position: "", dni: "", dni_expiry: "", phone: "", email: "", photo: "", hire_date: "", end_date: "", active: true, clothing_size: "", shoe_size: "", iban: "",
  tools: [], vehicles: [], medical_checks: [], prl_courses: [], epis: [],
};

const SECTIONS = {
  tools: {
    icon: Wrench, label: "Herramientas", add: "Añadir herramienta",
    blank: () => ({ name: "", serial: "", delivery_date: "", return_date: "", status: "Prestada", value: "" }),
    fields: [
      { k: "name", l: "Herramienta" }, { k: "serial", l: "Nº Serie" },
      { k: "delivery_date", l: "Entrega", t: "date" }, { k: "return_date", l: "Devolución", t: "date" },
      { k: "value", l: "Valor" }, { k: "status", l: "Estado" },
    ],
  },
  vehicles: {
    icon: Truck, label: "Vehículo", add: "Añadir vehículo",
    blank: () => ({ plate: "", brand: "", model: "", vtype: "Furgoneta", assign_date: "", mileage: "", next_itv: "", label: "" }),
    fields: [
      { k: "plate", l: "Matrícula", testid: "vehicle-plate-input" }, { k: "brand", l: "Marca" },
      { k: "model", l: "Modelo" }, { k: "vtype", l: "Tipo" },
      { k: "assign_date", l: "Asignación", t: "date" }, { k: "mileage", l: "Kilometraje" },
      { k: "next_itv", l: "Próxima ITV", t: "date" }, { k: "label", l: "Etiqueta DGT" },
    ],
  },
  medical_checks: {
    icon: Stethoscope, label: "Reconoc. Médicos", add: "Añadir reconocimiento",
    blank: () => ({ result: "Apto", date: "", expiry: "", center: "" }),
    fields: [
      { k: "result", l: "Resultado" }, { k: "center", l: "Centro médico" },
      { k: "date", l: "Fecha realización", t: "date" }, { k: "expiry", l: "Caducidad", t: "date" },
    ],
  },
  prl_courses: {
    icon: GraduationCap, label: "Cursos PRL", add: "Añadir curso",
    blank: () => ({ name: "", entity: "", date: "", expiry: "" }),
    fields: [
      { k: "name", l: "Nombre del curso" }, { k: "entity", l: "Entidad" },
      { k: "date", l: "Fecha realización", t: "date" }, { k: "expiry", l: "Caducidad", t: "date" },
    ],
  },
  epis: {
    icon: HardHat, label: "EPIs", add: "Añadir EPI",
    blank: () => ({ etype: "", brand: "", delivery_date: "", expiry: "", review_date: "", next_review: "" }),
    fields: [
      { k: "etype", l: "Denominación del EPI" }, { k: "brand", l: "Marca" },
      { k: "delivery_date", l: "Fecha entrega", t: "date" }, { k: "expiry", l: "Fecha caducidad (vacío = por uso)", t: "date" },
      { k: "review_date", l: "Fecha revisado", t: "date" }, { k: "next_review", l: "Próxima revisión", t: "date" },
    ],
  },
  clothing: {
    icon: Shirt, label: "Ropa y calzado", add: "Añadir prenda / calzado",
    blank: () => ({ item: "", size: "", qty: "1", delivery_date: "", notes: "" }),
    fields: [
      { k: "item", l: "Prenda / calzado", testid: "clothing-item-input" }, { k: "size", l: "Talla", testid: "clothing-size-input" },
      { k: "qty", l: "Cantidad" }, { k: "delivery_date", l: "Fecha entrega", t: "date" },
      { k: "notes", l: "Notas" },
    ],
  },
};

function RepeatList({ section, items, disabled, onChange, employeeId, attachments, onFilesChanged, onSign, catalog, onReview }) {
  const cfg = SECTIONS[section];
  const attachable = ["prl_courses", "medical_checks", "epis"].includes(section);
  const update = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  const add = () => onChange([...items, cfg.blank()]);
  const remove = (i) => onChange(items.filter((_, x) => x !== i));
  const unsignedEpis = section === "epis" ? items.filter((p) => p.id && !p.signed_at) : [];
  const today = new Date().toISOString().slice(0, 10);
  const catItems = (catalog || []).filter((c) => (section === "epis" ? c.kind !== "ropa" : c.kind === "ropa"));
  const addFromCatalog = (id) => {
    const c = catItems.find((x) => x.id === id);
    if (!c) return;
    onChange([...items, section === "epis"
      ? { ...cfg.blank(), etype: c.name, brand: c.brand, delivery_date: today }
      : { ...cfg.blank(), item: c.name, delivery_date: today, notes: c.brand }]);
  };

  return (
    <div className="space-y-3">
      {section === "epis" && employeeId && !disabled && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
          <span className="text-amber-900">{unsignedEpis.length} EPI(s) guardados pendientes de firma de entrega.</span>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" disabled={items.filter((p) => p.id).length === 0} onClick={onReview}
              data-testid="epi-review-button"><RefreshCw className="h-4 w-4 mr-1.5" /> Revisión anual</Button>
            <Button type="button" size="sm" variant="outline" disabled={unsignedEpis.length === 0} onClick={() => onSign(unsignedEpis)}
              data-testid="sign-epis-button"><PenLine className="h-4 w-4 mr-1.5" /> Firmar entrega</Button>
          </div>
        </div>
      )}
      {items.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Sin registros.</p>}
      {items.map((item, i) => {
        const files = attachable ? (attachments || []).filter((f) => f.item_id === item.id || (f.epi_ids || []).includes(item.id)) : [];
        return (
        <div key={item.id || i} className="rounded-lg border p-3 bg-slate-50/60 relative">
          <div className="grid sm:grid-cols-2 gap-3">
            {cfg.fields.map((f) => (
              <div key={f.k} className="space-y-1">
                <Label className="text-xs">{f.l}</Label>
                <Input type={f.t || "text"} value={item[f.k] || ""} disabled={disabled}
                  data-testid={f.testid}
                  onChange={(e) => update(i, f.k, e.target.value)} className="h-9 bg-white" />
              </div>
            ))}
          </div>
          {section === "epis" && item.signed_at && (
            <p className="mt-2 text-xs text-emerald-700 flex items-center gap-1" data-testid="epi-signed-badge">
              <PenLine className="h-3 w-3" /> Entrega firmada el {new Date(item.signed_at).toLocaleDateString("es-ES")}
            </p>
          )}
          {attachable && employeeId && (
            <div className="mt-2 space-y-1.5">
              <AttachmentList files={files} isAdmin={!disabled} onDeleted={onFilesChanged} />
              {!disabled && section !== "epis" && (
                item.id
                  ? <UploadButton employeeId={employeeId} section={section} itemId={item.id} onUploaded={onFilesChanged} />
                  : <p className="text-[11px] text-muted-foreground">Guarda la ficha para poder adjuntar el certificado.</p>
              )}
            </div>
          )}
          {!disabled && (
            <Button type="button" size="icon" variant="ghost" onClick={() => remove(i)}
              className="absolute top-2 right-2 h-7 w-7 text-red-500 hover:text-red-600">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        );
      })}
      {!disabled && (
        <div className="flex flex-wrap gap-2 items-center">
          <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`add-${section}-button`}>
            <Plus className="h-4 w-4 mr-2" /> {cfg.add}
          </Button>
          {catItems.length > 0 && (
            <select className="h-9 rounded-md border bg-white px-2 text-sm" value="" onChange={(e) => addFromCatalog(e.target.value)}
              data-testid={`catalog-select-${section}`}>
              <option value="">＋ Añadir del catálogo…</option>
              {catItems.map((c) => <option key={c.id} value={c.id}>{c.name}{c.brand ? ` (${c.brand})` : ""}</option>)}
            </select>
          )}
        </div>
      )}
    </div>
  );
}

export default function EmployeeDialog({ open, employee, isAdmin, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const disabled = !isAdmin;

  useEffect(() => {
    if (open) setForm(employee ? { ...EMPTY, ...employee } : { ...EMPTY });
  }, [open, employee]);

  const [attachments, setAttachments] = useState([]);
  const [history, setHistory] = useState([]);
  const [signing, setSigning] = useState(null);
  const [catalog, setCatalog] = useState([]);
  useEffect(() => { if (open && isAdmin) api.get("/catalog").then((r) => setCatalog(r.data)).catch(() => {}); }, [open, isAdmin]);

  const annualReview = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const d = window.prompt("Fecha de la revisión (AAAA-MM-DD). La próxima revisión se fijará 12 meses después.", today);
    if (!d) return;
    try {
      const r = await api.post(`/employees/${employee.id}/epi-review`, { review_date: d, months: 12 });
      toast.success(`${r.data.reviewed} EPIs revisados · próxima revisión ${r.data.next_review.split("-").reverse().join("/")}`);
      const fresh = await api.get(`/employees/${employee.id}`);
      setForm((f) => ({ ...f, epis: fresh.data.epis }));
      loadExtras(); onSaved();
      downloadEpiSheet();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const loadExtras = () => {
    if (!employee?.id) { setAttachments([]); setHistory([]); return; }
    api.get(`/employees/${employee.id}/attachments`).then((r) => setAttachments(r.data)).catch(() => {});
    api.get(`/employees/${employee.id}/history`).then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { if (open) loadExtras(); }, [open, employee]); // eslint-disable-line react-hooks/exhaustive-deps

  const signEpis = async (dataUrl, signer) => {
    try {
      const r = await api.post(`/employees/${employee.id}/epi-receipt`,
        { epi_ids: signing.map((p) => p.id), signature: dataUrl, signer_name: signer });
      toast.success("Recibo de EPIs firmado y guardado");
      setSigning(null);
      const fresh = await api.get(`/employees/${employee.id}`);
      setForm((f) => ({ ...f, epis: fresh.data.epis }));
      loadExtras();
      onSaved();
      openFile(r.data.id, r.data.original_filename);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo firmar");
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [pdfBusy, setPdfBusy] = useState(false);
  const [portalOpen, setPortalOpen] = useState(false);
  const photoRef = useRef();

  const downloadBlob = async (path, fallback) => {
    setPdfBusy(true);
    try {
      const res = await api.get(path, { responseType: "blob" });
      const m = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : fallback; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo generar el PDF"); }
    finally { setPdfBusy(false); }
  };
  const downloadPdf = () => downloadBlob(`/employees/${employee.id}/pdf`, "ficha.pdf");
  const downloadEpiSheet = () => downloadBlob(`/employees/${employee.id}/epi-sheet`, "ficha_epi.pdf");

  const save = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      if (employee) await api.put(`/employees/${employee.id}`, form);
      else await api.post("/employees", form);
      toast.success(employee ? "Empleado actualizado" : "Empleado creado");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl w-[calc(100vw-1rem)] sm:w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col p-4 sm:p-6" data-testid="employee-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {employee ? (disabled ? "Ficha del empleado" : "Editar empleado") : "Nuevo empleado"}
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="general" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="w-full justify-start overflow-x-auto flex-wrap h-auto">
            <TabsTrigger value="general" data-testid="tab-general"><User className="h-4 w-4 mr-1.5" /> General</TabsTrigger>
            {Object.entries(SECTIONS).map(([k, c]) => (
              <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}>
                <c.icon className="h-4 w-4 mr-1.5" /> {c.label}
                {form[k]?.length > 0 && <span className="ml-1.5 text-xs bg-accent text-white rounded-full px-1.5">{form[k].length}</span>}
              </TabsTrigger>
            ))}
            {employee && (
              <TabsTrigger value="documents" data-testid="tab-documents"><FolderOpen className="h-4 w-4 mr-1.5" /> Documentos</TabsTrigger>
            )}
            {employee && (
              <TabsTrigger value="history" data-testid="tab-history"><History className="h-4 w-4 mr-1.5" /> Historial
                {history.length > 0 && <span className="ml-1.5 text-xs bg-slate-400 text-white rounded-full px-1.5">{history.length}</span>}
              </TabsTrigger>
            )}
          </TabsList>

          <div className="flex-1 overflow-y-auto py-4 pr-1">
            <TabsContent value="general" className="mt-0 grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 flex items-center gap-4">
                <div className="h-20 w-20 rounded-full bg-accent/10 border border-accent/30 flex items-center justify-center font-semibold text-accent text-xl overflow-hidden shrink-0">
                  {form.photo ? <img src={form.photo} alt="" className="h-full w-full object-cover" data-testid="employee-photo-preview" /> : initials(form.name || "?")}
                </div>
                {!disabled && (
                  <div className="space-y-1">
                    <input ref={photoRef} type="file" accept="image/*" capture="user" className="hidden" data-testid="employee-photo-input"
                      onChange={async (e) => {
                        const f = e.target.files?.[0]; e.target.value = "";
                        if (!f) return;
                        try { set("photo", await resizeImage(f)); } catch { toast.error("No se pudo leer la imagen"); }
                      }} />
                    <div className="flex gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={() => photoRef.current?.click()} data-testid="employee-photo-button">
                        <Camera className="h-4 w-4 mr-1.5" /> {form.photo ? "Cambiar foto" : "Añadir foto"}
                      </Button>
                      {form.photo && <Button type="button" size="sm" variant="ghost" onClick={() => set("photo", "")}>Quitar</Button>}
                    </div>
                    <p className="text-[11px] text-muted-foreground">JPG/PNG. Se ajusta automáticamente a 320 px.</p>
                  </div>
                )}
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Nombre completo *</Label>
                <Input value={form.name} disabled={disabled} onChange={(e) => set("name", e.target.value)}
                  data-testid="employee-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Puesto</Label>
                <Input value={form.position} disabled={disabled} onChange={(e) => set("position", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Teléfono</Label>
                <Input value={form.phone} disabled={disabled} onChange={(e) => set("phone", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>DNI / NIE</Label>
                <Input value={form.dni} disabled={disabled} onChange={(e) => set("dni", e.target.value)}
                  data-testid="employee-dni-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Caducidad del DNI</Label>
                <Input type="date" value={form.dni_expiry} disabled={disabled}
                  onChange={(e) => set("dni_expiry", e.target.value)} data-testid="employee-dni-expiry-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Talla de ropa</Label>
                <Input value={form.clothing_size} disabled={disabled} placeholder="Ej. L / 52" onChange={(e) => set("clothing_size", e.target.value)} data-testid="employee-clothing-size-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Talla de calzado</Label>
                <Input value={form.shoe_size} disabled={disabled} placeholder="Ej. 43" onChange={(e) => set("shoe_size", e.target.value)} data-testid="employee-shoe-size-input" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Email</Label>
                <Input type="email" value={form.email} disabled={disabled} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>IBAN / cuenta bancaria</Label>
                <Input value={form.iban} disabled={disabled} placeholder="ES00 0000 0000 0000 0000 0000"
                  onChange={(e) => set("iban", e.target.value)} data-testid="employee-iban-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Fecha de alta</Label>
                <Input type="date" value={form.hire_date} disabled={disabled}
                  onChange={(e) => set("hire_date", e.target.value)} data-testid="employee-hire-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Fecha de baja</Label>
                <Input type="date" value={form.end_date} disabled={disabled}
                  onChange={(e) => set("end_date", e.target.value)} data-testid="employee-end-date-input" />
              </div>
              <div className="flex items-center gap-3 sm:col-span-2">
                <input type="checkbox" id="emp-active" checked={form.active !== false} disabled={disabled}
                  onChange={(e) => set("active", e.target.checked)} className="h-4 w-4 accent-primary"
                  data-testid="employee-active-checkbox" />
                <Label htmlFor="emp-active">Empleado activo</Label>
              </div>
            </TabsContent>

            {Object.keys(SECTIONS).map((k) => (
              <TabsContent key={k} value={k} className="mt-0">
                <RepeatList section={k} items={form[k] || []} disabled={disabled}
                  onChange={(v) => set(k, v)} employeeId={employee?.id} attachments={attachments}
                  onFilesChanged={loadExtras} onSign={setSigning} catalog={catalog} onReview={annualReview} />
              </TabsContent>
            ))}
            {employee && (
              <TabsContent value="documents" className="mt-0">
                <EmployeeDocuments employeeId={employee.id} isAdmin={isAdmin} employee={employee} />
              </TabsContent>
            )}
            {employee && (
              <TabsContent value="history" className="mt-0">
                <HistoryList entries={history} />
              </TabsContent>
            )}
          </div>
        </Tabs>

        <SignaturePad open={!!signing} title="Firma de entrega de EPIs" employeeName={form.name}
          items={(signing || []).map((p) => `${p.etype}${p.delivery_date ? ` (entregado ${p.delivery_date})` : ""}`)}
          onClose={() => setSigning(null)} onConfirm={signEpis} />
        {employee && (
          <PortalAccessDialog open={portalOpen} employee={employee} onClose={() => setPortalOpen(false)} onChanged={onSaved} />
        )}

        <DialogFooter className="flex-row flex-wrap gap-2 justify-end">
          {employee && (
            <div className="mr-auto flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={downloadPdf} disabled={pdfBusy} data-testid="employee-pdf-button">
                {pdfBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />} Ficha PDF
              </Button>
              <Button variant="outline" size="sm" onClick={downloadEpiSheet} disabled={pdfBusy} data-testid="employee-epi-sheet-button">
                <ClipboardList className="h-4 w-4 mr-2" /> Ficha EPIs
              </Button>
              {!disabled && (
                <Button variant="outline" size="sm" onClick={() => setPortalOpen(true)} data-testid="portal-access-button">
                  <KeyRound className="h-4 w-4 mr-2" /> {employee.portal_enabled ? "Acceso portal ✓" : "Acceso portal"}
                </Button>
              )}
            </div>
          )}
          <Button variant="outline" onClick={onClose}>{disabled ? "Cerrar" : "Cancelar"}</Button>
          {!disabled && (
            <Button onClick={save} disabled={saving} data-testid="save-employee-button">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Guardar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
